<template>
  <footer class="bg-black-900 border-t border-gold-800 mt-20">
    <div class="container mx-auto px-6 py-12">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-12">
        <!-- Logo and description -->
        <div class="col-span-1 md:col-span-2">
          <div class="flex items-center space-x-3 mb-2">
          <div class="w-16 h-16 rounded-full flex items-center justify-center">
            <img src="@/assets/logo.png" alt="Canada Smile Logo" class="w-16 h-16">
          </div>
            <div>
              <h2 class="text-2xl font-display font-bold text-gold-400 mb-2">Canada Smile Lab</h2>
            </div>
          </div>
          
          <!-- Social Media in Footer -->
          <div class="flex space-x-4">
            <a href="https://www.instagram.com/canada_smile?igsh=d3Jjam15dGJ4MGli" target="_blank" rel="noopener noreferrer" 
               class="w-10 h-10 border border-gold-500 rounded-full flex items-center justify-center text-gold-400 hover:bg-gold-500 hover:text-black-900 transition-colors duration-300">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/share/17SJCm2W7t/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer"
               class="w-10 h-10 border border-gold-500 rounded-full flex items-center justify-center text-gold-400 hover:bg-gold-500 hover:text-black-900 transition-colors duration-300">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="http://www.tiktok.com/@canadasmilelab" target="_blank" rel="noopener noreferrer"
               class="w-10 h-10 border border-gold-500 rounded-full flex items-center justify-center text-gold-400 hover:bg-gold-500 hover:text-black-900 transition-colors duration-300">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .6.05.89.14V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div>
          <h3 class="text-xl font-display font-bold text-gold-400 mb-6">Quick Links</h3>
          <ul class="space-y-4">
            <li v-for="item in quickLinks" :key="item.name">
              <router-link :to="item.path" class="text-gray-400 hover:text-gold-400 transition-colors duration-300 !outline-none !ring-0">
                {{ item.name }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div>
          <h3 class="text-xl font-display font-bold text-gold-400 mb-6">Contact Info</h3>
          <ul class="space-y-4">
            <li class="flex items-start space-x-3 text-gray-400">
              <svg class="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <span class="block">305 Renfrew Dr #102</span>
                <span class="block">Markham, ON L3R 9S7</span>
                <span class="block">Canada</span>
              </div>
            </li>
            <li class="flex items-center space-x-3 text-gray-400">
              <svg class="w-5 h-5 text-gold-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>+1 (416) 727-9598</span>
            </li>
            <li class="flex items-center space-x-3 text-gray-400">
              <svg class="w-5 h-5 text-gold-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>info@canadasmilelab.com</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="border-t border-gold-800 mt-12 pt-8 text-center text-gray-500">
        <p>&copy; {{ currentYear }} Canada Smile Lab</p>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'Footer',
  data() {
    return {
      currentYear: new Date().getFullYear(),
      quickLinks: [
        { name: 'Home', path: '/' },
        { name: 'Products', path: '/services' },
        { name: 'Gallery', path: '/gallery'},
        { name: 'About Us', path: '/about' },
        { name: 'RX Form', path: '/RXform'},
        { name: 'Contact', path: '/contact' }
      ]
    }
  }
}
</script>